import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { initNativeShell } from "@/lib/native";
import { triggerFcmDueCheck, currentFcmToken } from "@/lib/fcm";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AddSheetProvider } from "@/context/AddSheetContext";
import { HeaderExtrasProvider } from "@/context/HeaderExtras";
import { Header } from "@/components/Header";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { BottomNav } from "@/components/BottomNav";
import { Protected } from "@/components/Protected";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ChequeDuePrompt } from "@/components/ChequeDuePrompt";
import { ChequeOverdraftModal } from "@/components/ChequeOverdraftModal";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Splash from "@/pages/Splash";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
// Rarely-used pages are lazy-loaded to shrink first-paint bundle.
const Budgets = lazy(() => import("@/pages/Budgets"));
const Account = lazy(() => import("@/pages/Account"));
const Admin = lazy(() => import("@/pages/Admin"));
const Installments = lazy(() => import("@/pages/Installments"));
const Accounts = lazy(() => import("@/pages/Accounts"));
const Cheques = lazy(() => import("@/pages/Cheques"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));

export default function App() {
  useEffect(() => {
    initNativeShell();
    // Dynamically ensure iOS Safari picks up latest theme-color on every launch
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", "#eceef1");
    // Every time the app opens (foreground), ask the server to re-check due items.
    // Closes any gap left by container restarts on production.
    currentFcmToken().then((tok) => { if (tok) triggerFcmDueCheck(); }).catch(() => {});
  }, []);
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <AddSheetProvider>
              <HeaderExtrasProvider>
              <div className="neu-page min-h-screen pb-28">
                <Header />
                <SubscriptionBanner />
                <Toaster
                  position="top-center"
                  richColors={false}
                  closeButton
                  offset={`calc(env(safe-area-inset-top) + 12px)`}
                  toastOptions={{ duration: 3200 }}
                />
                <InstallPrompt />
                <ChequeDuePrompt />
                <ChequeOverdraftModal />
                <Routes>
                  <Route path="/" element={<Splash />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
                  <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
                  <Route path="/budgets" element={<Protected><Suspense><Budgets /></Suspense></Protected>} />
                  <Route path="/installments" element={<Protected><Suspense><Installments /></Suspense></Protected>} />
                  <Route path="/accounts" element={<Protected><Suspense><Accounts /></Suspense></Protected>} />
                  <Route path="/cheques" element={<Protected><Suspense><Cheques /></Suspense></Protected>} />
                  <Route path="/account" element={<Protected><Suspense><Account /></Suspense></Protected>} />
                  <Route path="/admin" element={<Protected adminOnly><Suspense><Admin /></Suspense></Protected>} />
                  <Route path="/payment/success" element={<Protected><Suspense><PaymentSuccess /></Suspense></Protected>} />
                  <Route path="/payment/cancel" element={<Protected><Suspense><PaymentCancel /></Suspense></Protected>} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                <BottomNav />
              </div>
              </HeaderExtrasProvider>
            </AddSheetProvider>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
